import * as $ from 'jquery';
import * as ko from 'knockout';

import { Event } from 'Core/Common/Event';
import { Modal } from 'Core/Common/Modal';
import { BlockUI } from 'Core/Common/BlockUi';
import { Notifier } from 'Core/Common/Notifier';

import { PUB_SUB_EVENTS } from 'MenuManager/PubSubEvents';

import { SecurityProfileStore } from 'Core/Common/Security/SecurityProfile/Stores/SecurityProfileStore';

import { LABELS, NOTIFICATIONS } from 'Core/Components/Translation/Locales';

import { ProfileSelectorStore } from './Stores/ProfileSelectorStore';
import { ProfileSelectorItem } from './Models/ProfileSelectorItem';

import Template from './Templates/Template.html';
import { GlobalManager, GLOBALS } from '../../GlobalManager/GlobalManager';

ko.templates['Core/Screens/ProfileSelectorScreen/Templates/Template'] = Template;

export class ProfileSelectorScreen extends Event {
    private _originalMainProfileId: number;

    private _modal: Modal;
    private _store: ProfileSelectorStore;

    private _labels: LABELS;
    private _closeBound: EventListener | null = null;

    Profiles: KnockoutObservableArray<ProfileSelectorItem>;
    SelectedProfile: KnockoutObservable<ProfileSelectorItem>;
    SelectedProfileId: KnockoutObservable<number>;
    Error: KnockoutObservable<string>;

    constructor(
        private _entityId: number,
        private _entityName?: string,
        private _defaultMailProfile?: string
    ) {
        super();

        this._store = new ProfileSelectorStore();
        this._labels = LABELS;
        this._closeBound = null;

        this.Profiles = ko.observableArray([]);
        this.SelectedProfile = ko.observable(null);
        this.SelectedProfileId = ko.observable(null);
        this.Error = ko.observable(null);
    }

    RenderOption = (item, escape) => {
        const icon = item.Main
            ? '<i class="fa-solid fa-circle-check ms-auto text-green-500 text-base" title="Active Profile"></i>'
            : '';
        const customClass = item.Main ? 'justify-between' : '';

        return `<div class='select-option ${customClass}'>${escape(item.Name)}${icon}</div>`;
    };

    RenderItem = (item, escape) => {
        const icon = item.Main
            ? '<i class="fa-solid fa-circle-check ms-auto text-green-500 text-base"></i>'
            : '';

        return `<div class='selected-item'>${escape(item.Name)}</div>`;
    };

    ProfileOptionAfterRender = (option: HTMLElement, item: ProfileSelectorItem) => {
        if (!item) {
            return;
        }

        (option as HTMLElement).setAttribute('data-data', JSON.stringify(item));
    };

    GetTemplateName() {
        return 'Core/Screens/ProfileSelectorScreen/Templates/Template';
    }

    AfterRender() {}

    ShowIfNeeded() {
        if (!this.HasToBeShownByGlobal() || this.IsCdTable() || this.IsSysTable()) {
            this.Trigger('USED_CURRENT_PROFILE');
            return;
        }

        BlockUI.Block();
        this.LoadCreatorProfiles()
            .then(() => this.ResolveCreatorProfiles())
            .fail((err) => new Notifier().Failed(err.message))
            .always(() => BlockUI.Unblock());
    }

    SelectMainProfile(profileId: number, backgroundSelection?: boolean) {
        if (!profileId) {
            new Notifier().Failed('Profile ID is missing');
            return;
        }
        SecurityProfileStore.SelectMainProfile(profileId)
            .then(() => {
                PubSub.publish(PUB_SUB_EVENTS.REFRESH_SECURITY_PROFILE, null);
                if (backgroundSelection) {
                    this.Trigger('PROFILE_BACKGROUND_SELECTED');
                } else {
                    this.Trigger('PROFILE_SELECTED');
                }
            })
            .fail((error) => new Notifier().Failed(JSON.parse(error.message).Message));
    }

    OnConfirm() {
        if (!this.SelectedProfile()) {
            this.Error(LABELS.PLEASE_SELECT_THE_PROFILE);
            return;
        }

        if (this._originalMainProfileId === this.SelectedProfile().Id) {
            this.Trigger('USED_CURRENT_PROFILE');
            return;
        }
        this.SelectMainProfile(this.SelectedProfile().Id);
    }

    OnCancel() {
        this.Trigger('PROFILE_SELECTION_CANCELLED');
    }

    Close() {
        if (!this._modal) {
            return;
        }

        this._modal.Close();
        this._modal.Destroy();
    }

    CloseModal(): void {
        this.Close();
        this.Trigger('PROFILE_SELECTOR_CLOSE_MODAL');
    }

    private LoadCreatorProfiles() {
        return this._store
            .GetCreatorProfiles(this._entityId)
            .then((profiles: ProfileSelectorItem[]) => {
                const selectedProfile: ProfileSelectorItem = profiles.find(
                    (profile) => profile.Main
                );
                this._originalMainProfileId = selectedProfile && selectedProfile.Id;

                if (selectedProfile) {
                    this.SelectedProfileId(selectedProfile.Id);
                    this.SelectedProfile(selectedProfile);
                }

                this.Profiles(profiles);
            })
            .fail((error) => new Notifier().Failed(error.message));
    }

    private ResolveCreatorProfiles() {
        const profiles: ProfileSelectorItem[] = this.Profiles();
        const defaultMailProfile: string = this._defaultMailProfile;

        if (profiles.length === 0) {
            this.Trigger('PROFILES_NOT_FOUND');
            return;
        }
        if (profiles.length === 1) {
            this.Trigger('USED_CURRENT_PROFILE');
            return;
        }

        let isBackgroundSelection = null;
        let profileFound: boolean = null;
        if (defaultMailProfile) {
            _.each(profiles, (profile: ProfileSelectorItem) => {
                if (profile.Name === defaultMailProfile) {
                    profileFound = true;
                    if (!profile.Main) {
                        isBackgroundSelection = true;
                        this.SelectMainProfile(profile.Id, isBackgroundSelection);
                    } else {
                        isBackgroundSelection = true;
                        this.Trigger('PROFILE_BACKGROUND_SELECTED');
                    }
                }
            });

            if (!profileFound) {
                let text = NOTIFICATIONS.PROFILE_DOES_NOT_EXIST;
                new Notifier().Warning(text.replace('{profile}', `<b>${defaultMailProfile}</b>`));
            }
        }

        if (!isBackgroundSelection) {
            this.RenderSelector();
        }
    }

    private RenderSelector() {
        let self = this;
        this._modal = new Modal(
            {
                width: 400,
                closeOnEsc: false,
                onOpen: function () {
                    const button = this.closeButton[0];
                    $(button).off('click');
                    button.addEventListener(
                        'click',
                        (self._closeBound = self.CloseModal.bind(self))
                    );
                },
                onClose: function () {
                    const button = this.closeButton[0];
                    if (self._closeBound) {
                        button.removeEventListener('click', self._closeBound);
                        self._closeBound = null;
                    }
                },
            },
            false
        );

        ko.cleanNode(this._modal.Wrapper);
        ko.applyBindings(this, this._modal.Wrapper);

        this._modal.Show();
    }

    private HasToBeShownByGlobal() {
        return GlobalManager.Instance.GetGlobal(GLOBALS.SHOW_PROFILE_SELECTOR) === '1';
    }

    private IsCdTable() {
        return this._entityName && this._entityName.startsWith('CD_');
    }

    private IsSysTable() {
        return this._entityName && this._entityName.startsWith('SYS_');
    }
}
